import { useEffect, useState } from 'react';
import { Package } from 'lucide-react';

/**
 * A product photo that always renders something.
 *
 * Photos in this app come from three places — an uploaded object, a seeded
 * placeholder URL, and nothing at all — and two of those can fail at paint time
 * (a bucket hiccup, an offline warehouse). So the fallback is not an error
 * state: it is a generated tile, coloured deterministically from the product's
 * own name, showing its initials. A list of a hundred rows stays legible and
 * scannable even with every image request failing.
 */
const TILE_COLORS = [
  ['#eef2ff', '#4338ca'],
  ['#ecfeff', '#0e7490'],
  ['#f0fdf4', '#15803d'],
  ['#fff7ed', '#c2410c'],
  ['#fdf4ff', '#a21caf'],
  ['#fef2f2', '#be123c'],
  ['#f8fafc', '#334155'],
  ['#fffbeb', '#b45309'],
];

function hashOf(text) {
  let h = 0;
  for (let i = 0; i < String(text).length; i++) h = (h * 31 + String(text).charCodeAt(i)) % 100000;
  return h;
}

function initialsOf(name) {
  const words = String(name || '?')
    .replace(/[^a-zA-Z0-9\s]/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return '?';
  return (words[0][0] + (words[1] ? words[1][0] : '')).toUpperCase();
}

export default function ProductImage({ src, name, className = '', rounded = 'rounded-xl' }) {
  const [failed, setFailed] = useState(false);

  // A new src deserves a fresh attempt — otherwise replacing a broken photo
  // leaves the fallback showing until a full remount.
  useEffect(() => setFailed(false), [src]);

  const [bg, fg] = TILE_COLORS[hashOf(name) % TILE_COLORS.length];

  if (!src || failed) {
    return (
      <div
        className={`flex items-center justify-center overflow-hidden ${rounded} ${className}`}
        style={{ background: bg, color: fg }}
        aria-label={name ? `${name} (no photo)` : 'No photo'}
      >
        <div className="flex flex-col items-center gap-1">
          <Package size="35%" strokeWidth={1.5} className="opacity-40" />
          <span className="text-[clamp(0.7rem,22%,1.6rem)] font-extrabold leading-none tracking-tight">
            {initialsOf(name)}
          </span>
        </div>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={name || 'Product photo'}
      loading="lazy"
      onError={() => setFailed(true)}
      className={`object-cover ${rounded} ${className}`}
    />
  );
}
